import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  Platform,
  Image,
  Modal,
  useWindowDimensions } from 'react-native'
import {
  Search, X, Plus, Minus, Trash2, CreditCard, Banknote,
  Smartphone, Check, ShoppingCart, ScanBarcode, ArrowLeft,
  Monitor, Camera, Link2, Unlink, QrCode, Copy, RefreshCw, Package,
  Receipt, Mail, SkipForward, Printer, CheckCircle, UserCheck } from 'lucide-react-native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { supabase, RealtimeChannel } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../components/Toast'
import { parseCustomerQR } from '../components/CustomerQR'
import theme from '../theme'
import { CameraView, useCameraPermissions } from 'expo-camera'
import {
  Card,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'

const merchantColors = {
  primary: '#C07A5F',
  primaryLight: '#D4A088',
  accent: '#748C70' }

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MerchantPOS'>
  route: { params: { storeId: string } }
}

interface Product {
  id: string
  title: string
  title_fi?: string
  price: number
  barcode?: string
  image_url?: string
  status: string
  ownership_type: string
  seller_id?: string
}

interface CartItem {
  id?: string
  product: Product
  quantity: number
}

interface POSSession {
  id: string
  session_code: string
  store_id: string
  status: string
  created_at: string
}

type PaymentMethod = 'cash' | 'card' | 'mobilepay'
type POSMode = 'standalone' | 'register' | 'scanner'

export default function MerchantPOSScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { t, language } = useLanguage()
  const { user } = useAuth()
  const { width: screenWidth } = useWindowDimensions()
  const isNarrow = screenWidth < 700

  // On narrow screens, tab between search and cart
  const [activeTab, setActiveTab] = useState<'search' | 'cart'>('search')

  // Mode state
  const [mode, setMode] = useState<POSMode>('standalone')
  const [session, setSession] = useState<POSSession | null>(null)
  const [joinCode, setJoinCode] = useState('')
  const [showModeModal, setShowModeModal] = useState(false)
  const [connecting, setConnecting] = useState(false)

  // Camera state
  const [permission, requestPermission] = useCameraPermissions()
  const [scanned, setScanned] = useState(false)
  const [lastScannedCode, setLastScannedCode] = useState('')

  // Cart & search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Product[]>([])
  const [cart, setCart] = useState<CartItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searching, setSearching] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card')
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<PaymentMethod[]>(['card', 'cash', 'mobilepay'])
  const [processing, setProcessing] = useState(false)
  const [cashReceived, setCashReceived] = useState('')
  const [storeName, setStoreName] = useState('')

  // Inventory browse mode
  const [showInventory, setShowInventory] = useState(false)
  const [inventoryProducts, setInventoryProducts] = useState<Product[]>([])
  const [inventoryLoading, setInventoryLoading] = useState(false)

  // Post-payment receipt flow
  const [showReceipt, setShowReceipt] = useState(false)
  const [receiptStep, setReceiptStep] = useState<'customer' | 'done'>('customer')
  const [customerEmail, setCustomerEmail] = useState('')
  const [emailLooking, setEmailLooking] = useState(false)
  const [lastSaleTotal, setLastSaleTotal] = useState(0)
  const [lastSaleItems, setLastSaleItems] = useState(0)
  const [lastSaleId, setLastSaleId] = useState<string | null>(null)
  const [receiptCart, setReceiptCart] = useState<CartItem[]>([])

  // Completed transaction bar + detail modal
  interface CompletedSale {
    id: string; total: number; itemCount: number; receiptNumber: string
    cart: CartItem[]; buyerId: string | null; buyerName: string | null; buyerEmail: string | null; completedAt: Date
  }
  const [completedSale, setCompletedSale] = useState<CompletedSale | null>(null)
  const [showTransactionDetail, setShowTransactionDetail] = useState(false)
  const [txEmail, setTxEmail] = useState('')
  const [txEmailLooking, setTxEmailLooking] = useState(false)
  const [txScanningQR, setTxScanningQR] = useState(false)

  // Customer identification (from QR scan)
  const [pendingBuyerId, setPendingBuyerId] = useState<string | null>(null)
  const [pendingBuyerName, setPendingBuyerName] = useState<string | null>(null)
  const [scanningCustomerQR, setScanningCustomerQR] = useState(false)
  const toast = useToast()

  const searchInputRef = useRef<TextInput>(null)
  const searchTimeout = useRef<NodeJS.Timeout | null>(null)
  const channelRef = useRef<RealtimeChannel | null>(null)

  useEffect(() => {
    loadStoreInfo()
    return () => {
      // Cleanup realtime subscription
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
      }
    }
  }, [storeId])

  // Subscribe to cart changes when in multi-device mode
  useEffect(() => {
    if (session && (mode === 'register' || mode === 'scanner')) {
      subscribeToCartChanges()
    }
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [session, mode])

  // Fix web camera for barcode scanning: apply high resolution + continuous autofocus.
  // expo-camera web never requests specific resolution (uses browser default, often 640x480)
  // and never calls applyConstraints for focus. Both hurt QR/barcode scanning on mobile.
  useEffect(() => {
    if (Platform.OS !== 'web') return
    if (!permission?.granted) return

    const applyOptimalCameraSettings = async () => {
      try {
        const videos = document.querySelectorAll('video')
        for (const video of videos) {
          const stream = (video as any).srcObject as MediaStream | null
          if (!stream) continue
          const [track] = stream.getVideoTracks()
          if (!track) continue

          const caps = track.getCapabilities?.() as any
          const constraints: Record<string, any> = {}

          // Request highest available resolution for better barcode decoding
          if (caps?.width?.max) constraints.width = { ideal: caps.width.max }
          if (caps?.height?.max) constraints.height = { ideal: caps.height.max }

          // Apply basic constraints first (resolution)
          if (Object.keys(constraints).length > 0) {
            try { await track.applyConstraints(constraints) } catch { /* ignore */ }
          }

          // Apply focus via advanced constraints
          if (caps?.focusMode?.includes?.('continuous')) {
            try {
              await track.applyConstraints({ advanced: [{ focusMode: 'continuous' } as any] } as any)
            } catch { /* Focus API unsupported — ignore */ }
          }
        }
      } catch {
        // Silently ignore — camera API availability varies by browser/device
      }
    }

    // Give CameraView time to mount and open the stream
    const timer = setTimeout(applyOptimalCameraSettings, 800)
    return () => clearTimeout(timer)
  }, [mode, permission?.granted])

  const subscribeToCartChanges = async () => {
    if (!session) return

    // Remove existing subscription
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current)
    }

    const channel = supabase
      .channel(`pos_cart_${session.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pos_cart_items',
          filter: `session_id=eq.${session.id}` },
        async (payload) => {
          // Reload cart when changes occur
          await loadCartFromSession()
        }
      )
      .subscribe()

    channelRef.current = channel
  }

  const loadCartFromSession = async () => {
    if (!session) return

    try {
      const { data, error } = await supabase
        .from('pos_cart_items')
        .select(`
          id,
          quantity,
          product:products(
            id, title, title_fi, price, barcode, status, ownership_type, seller_id,
            product_images!product_images_product_id_fkey(image_url, is_primary)
          )
        `)
        .eq('session_id', session.id)

      if (error) throw error

      const cartItems: CartItem[] = (data || []).map((item: any) => {
        const product = item.product
        const primaryImage = product.product_images?.find((img: any) => img.is_primary) || product.product_images?.[0]
        return {
          id: item.id,
          quantity: item.quantity,
          product: {
            ...product,
            image_url: primaryImage?.image_url } }
      })

      setCart(cartItems)
    } catch (error) {
      console.error('Error loading cart:', error)
    }
  }

  const loadStoreInfo = async () => {
    const { data } = await supabase
      .from('flea_markets')
      .select('name, settings')
      .eq('id', storeId)
      .single()
    if (!data) return
    setStoreName(data.name)

    // Derive available payment methods from store amenities
    // amenity keys: card_payment, cash_payment, mobilepay → PaymentMethod: card, cash, mobilepay
    const amenities: string[] = data.settings?.amenities || []
    const PAYMENT_AMENITY_MAP: Record<string, PaymentMethod> = {
      card_payment: 'card',
      cash_payment: 'cash',
      mobilepay: 'mobilepay',
    }
    const fromAmenities = Object.entries(PAYMENT_AMENITY_MAP)
      .filter(([amenity]) => amenities.includes(amenity))
      .map(([, method]) => method)

    // Fall back to all methods if store hasn't configured amenities yet
    const methods: PaymentMethod[] = fromAmenities.length > 0
      ? fromAmenities
      : ['card', 'cash', 'mobilepay']

    setAvailablePaymentMethods(methods)
    // Default to card if available, otherwise first method
    setPaymentMethod(methods.includes('card') ? 'card' : methods[0])
  }

  const loadInventory = async () => {
    setInventoryLoading(true)
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id, title, title_fi, price, barcode, status, ownership_type, seller_id,
          product_images!product_images_product_id_fkey(image_url, is_primary)
        `)
        .eq('flea_market_id', storeId)
        .eq('status', 'for_sale')
        .order('title', { ascending: true })

      if (error) throw error

      const productsWithImages = (data || []).map(p => {
        const primaryImage = (p.product_images as any[])?.find(img => img.is_primary) || (p.product_images as any[])?.[0]
        return { ...p, image_url: primaryImage?.image_url }
      })
      setInventoryProducts(productsWithImages)
    } catch (error) {
      console.error('Error loading inventory:', error)
    } finally {
      setInventoryLoading(false)
    }
  }

  const openInventoryPicker = () => {
    loadInventory()
    setShowInventory(true)
  }

  // Session management
  const createSession = async () => {
    setConnecting(true)
    try {
      const { data, error } = await supabase
        .from('pos_sessions')
        .insert({
          store_id: storeId,
          created_by: user?.id,
          session_code: '', // Will be auto-generated by trigger
        })
        .select()
        .single()

      if (error) throw error

      setSession(data)
      setMode('register')
      setShowModeModal(false)
    } catch (error: any) {
      console.error('Error creating session:', error)
      showAlert(t('error'), error.message || 'Failed to create session')
    } finally {
      setConnecting(false)
    }
  }

  const joinSession = async () => {
    if (!joinCode.trim() || joinCode.length !== 6) {
      showAlert(t('error'), t('enter_session_code') || 'Please enter a 6-character session code')
      return
    }

    setConnecting(true)
    try {
      const { data, error } = await supabase
        .from('pos_sessions')
        .select('*')
        .eq('store_id', storeId)
        .eq('session_code', joinCode.toUpperCase())
        .eq('status', 'active')
        .single()

      if (error || !data) {
        throw new Error(t('session_not_found') || 'Session not found or expired')
      }

      setSession(data)
      setMode('scanner')
      setShowModeModal(false)

      // Request camera permission for scanner mode
      if (!permission?.granted) {
        await requestPermission()
      }
    } catch (error: any) {
      console.error('Error joining session:', error)
      showAlert(t('error'), error.message || 'Failed to join session')
    } finally {
      setConnecting(false)
    }
  }

  const endSession = async () => {
    if (!session) return

    try {
      await supabase
        .from('pos_sessions')
        .update({ status: 'completed' })
        .eq('id', session.id)

      // Clear cart items
      await supabase
        .from('pos_cart_items')
        .delete()
        .eq('session_id', session.id)

      setSession(null)
      setMode('standalone')
      setCart([])
    } catch (error) {
      console.error('Error ending session:', error)
    }
  }

  const copySessionCode = () => {
    if (session?.session_code && Platform.OS === 'web') {
      navigator.clipboard.writeText(session.session_code)
      showAlert(t('success'), t('code_copied') || 'Session code copied!')
    }
  }

  // Product search
  const searchProducts = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }

    setSearching(true)
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id, title, title_fi, price, barcode, status, ownership_type, seller_id,
          product_images!product_images_product_id_fkey(image_url, is_primary)
        `)
        .eq('flea_market_id', storeId)
        .eq('status', 'for_sale')
        .or(`barcode.eq.${query},title.ilike.%${query}%,title_fi.ilike.%${query}%`)
        .limit(20)

      if (error) throw error

      const productsWithImages = (data || []).map(p => {
        const primaryImage = (p.product_images as any[])?.find(img => img.is_primary) || (p.product_images as any[])?.[0]
        return {
          ...p,
          image_url: primaryImage?.image_url }
      })
      setSearchResults(productsWithImages)

      // Auto-add if exact barcode match
      if (productsWithImages.length === 1 && productsWithImages[0].barcode === query) {
        addToCart(productsWithImages[0])
        setSearchQuery('')
        setSearchResults([])
      }
    } catch (error) {
      console.error('Search error:', error)
    } finally {
      setSearching(false)
    }
  }

  const handleSearchChange = (text: string) => {
    setSearchQuery(text)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => searchProducts(text), 300)
  }

  // Cart operations — defined before handleBarcodeScanned so it's out of TDZ when used in deps
  const addToCart = useCallback(async (product: Product) => {
    if (session) {
      // Multi-device mode: add to database
      const existing = cart.find(item => item.product.id === product.id)
      if (existing && existing.id) {
        const { error } = await supabase
          .from('pos_cart_items')
          .update({ quantity: existing.quantity + 1 })
          .eq('id', existing.id)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('pos_cart_items')
          .insert({
            session_id: session.id,
            product_id: product.id,
            quantity: 1 })
        if (error) throw error
      }
      // Cart will update via realtime subscription
    } else {
      // Standalone mode: local state
      setCart(prev => {
        const existing = prev.find(item => item.product.id === product.id)
        if (existing) {
          return prev.map(item =>
            item.product.id === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        }
        return [...prev, { product, quantity: 1 }]
      })
      if (isNarrow) setActiveTab('cart')
    }
  }, [session, cart, user, setCart, isNarrow, setActiveTab])

  // Barcode scanning — handles both product barcodes and customer QR codes
  const handleBarcodeScanned = useCallback(async ({ data }: { data: string }) => {
    if (scanned || data === lastScannedCode) return

    setScanned(true)
    setLastScannedCode(data)

    // Check if this is a customer QR code (fliisi://customer/{uuid})
    const customerId = parseCustomerQR(data)
    if (customerId) {
      try {
        const { data: customer } = await supabase
          .from('users')
          .select('id, email, display_name, first_name, last_name')
          .eq('id', customerId)
          .maybeSingle()

        if (customer) {
          const name = customer.display_name
            || [customer.first_name, customer.last_name].filter(Boolean).join(' ')
            || customer.email?.split('@')[0]
            || 'Asiakas'

          // If we're in the transaction detail (post-sale QR scan), link to completed sale
          if (txScanningQR && completedSale) {
            await supabase.from('sales').update({ buyer_id: customer.id, buyer_email: customer.email }).eq('id', completedSale.id)
            setCompletedSale(prev => prev ? { ...prev, buyerId: customer.id, buyerName: name, buyerEmail: customer.email } : null)
            setTxScanningQR(false)
            toast.success(t('customer_found') || 'Asiakas tunnistettu', `${name} — ${t('receipt_in_app') || 'Kuitti näkyy sovelluksessa'}`)
            setScanned(false)
          // If we're in the receipt flow (scanning customer QR from modal), attach to last sale
          } else if (showReceipt && lastSaleId) {
            await supabase
              .from('sales')
              .update({ buyer_id: customer.id, buyer_email: customer.email })
              .eq('id', lastSaleId)

            toast.success(
              t('customer_found') || 'Asiakas tunnistettu',
              `${name} — ${t('receipt_in_app') || 'Kuitti näkyy sovelluksessa'}`
            )
            setScanningCustomerQR(false)
            finishReceiptFlow(customer.id, name, customer.email)
          } else {
            // Pre-sale: remember customer for when sale completes
            setPendingBuyerId(customer.id)
            setPendingBuyerName(name)
            toast.success(
              t('customer_identified') || 'Asiakas tunnistettu',
              `${name} — ${t('will_attach_to_sale') || 'Liitetään seuraavaan myyntiin'}`
            )
          }
        } else {
          toast.error(
            t('customer_not_found') || 'Asiakasta ei löytynyt',
            t('qr_not_registered') || 'QR-koodi ei vastaa rekisteröityä käyttäjää'
          )
        }
      } catch (error) {
        console.error('Customer QR lookup error:', error)
      }

      setTimeout(() => { setScanned(false); setLastScannedCode('') }, 1500)
      return
    }

    // Check if this is a Fliisi product URL QR tag (https://fliisi.fi/p/{uuid})
    const fliisiProductMatch = data.match(/fliisi\.fi\/p\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i)

    // Regular product lookup — by product ID (from QR tag) or barcode
    try {
      let query = supabase
        .from('products')
        .select(`
          id, title, title_fi, price, barcode, status, ownership_type, seller_id,
          product_images!product_images_product_id_fkey(image_url, is_primary)
        `)
        .eq('flea_market_id', storeId)
        .eq('status', 'for_sale')
        .limit(1)

      if (fliisiProductMatch) {
        query = query.eq('id', fliisiProductMatch[1])
      } else {
        query = query.eq('barcode', data)
      }

      const { data: products, error } = await query

      if (error) throw error

      if (products && products.length > 0) {
        const product = products[0]
        const primaryImage = (product.product_images as any[])?.find(img => img.is_primary) || (product.product_images as any[])?.[0]
        const productWithImage = { ...product, image_url: primaryImage?.image_url }
        await addToCart(productWithImage)
        const title = (language === 'fi' ? product.title_fi : null) || product.title || ''
        toast.success(t('item_added') || 'Tuote lisätty', title)
        if (isNarrow && !session) setActiveTab('cart')
      } else {
        showAlert(t('not_found'), t('product_not_found') || 'Tuotetta ei löydy')
      }
    } catch (error: any) {
      console.error('Barcode scan error:', error)
      toast.error('Virhe', error?.message || 'Tuotetta ei voitu lisätä koriin')
    }

    // Reset after delay to allow scanning another item
    setTimeout(() => {
      setScanned(false)
      setLastScannedCode('')
    }, 1500)
  }, [scanned, lastScannedCode, storeId, session, showReceipt, lastSaleId, isNarrow, setActiveTab, addToCart, language, toast])

  const updateQuantity = async (productId: string, delta: number) => {
    const item = cart.find(i => i.product.id === productId)
    if (!item) return

    const newQty = item.quantity + delta
    if (newQty <= 0) {
      removeFromCart(productId)
      return
    }

    if (session && item.id) {
      const { error } = await supabase
        .from('pos_cart_items')
        .update({ quantity: newQty })
        .eq('id', item.id)
      if (error) console.error('Error updating quantity:', error)
      // Cart updates via realtime
    } else {
      setCart(prev => prev.map(i =>
        i.product.id === productId ? { ...i, quantity: newQty } : i
      ))
    }
  }

  const removeFromCart = async (productId: string) => {
    const item = cart.find(i => i.product.id === productId)

    if (session && item?.id) {
      const { error } = await supabase
        .from('pos_cart_items')
        .delete()
        .eq('id', item.id)
      if (error) console.error('Error removing from cart:', error)
      // Cart updates via realtime
    } else {
      setCart(prev => prev.filter(item => item.product.id !== productId))
    }
  }

  const clearCart = async (saleId?: string) => {
    if (session) {
      if (saleId) {
        // Link cart items to the completed sale instead of deleting them
        const { error } = await supabase
          .from('pos_cart_items')
          .update({ sale_id: saleId })
          .eq('session_id', session.id)
        if (error) console.error('Error linking cart to sale:', error)
      } else {
        // No sale (e.g. manual clear) — delete
        const { error } = await supabase
          .from('pos_cart_items')
          .delete()
          .eq('session_id', session.id)
        if (error) console.error('Error clearing cart:', error)
      }
      // Cart updates via realtime
    } else {
      setCart([])
    }
  }

  const cartTotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0)
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0)

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}\n\n${message}`)
    } else {
      Alert.alert(title, message)
    }
  }

  // Generate receipt number: STORE-YYYYMMDD-NNNN
  const generateReceiptNumber = () => {
    const now = new Date()
    const date = now.getFullYear().toString() +
      String(now.getMonth() + 1).padStart(2, '0') +
      String(now.getDate()).padStart(2, '0')
    const seq = String(now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()).padStart(5, '0')
    const rand = String(Math.floor(Math.random() * 100)).padStart(2, '0')
    return `R-${date}-${seq}${rand}`
  }

  const processPayment = async () => {
    if (cart.length === 0) return

    setProcessing(true)
    setCompletedSale(null) // clear previous transaction bar when starting new sale
    try {
      const saleTotal = cartTotal
      const saleItemCount = cartItemCount
      const receiptNumber = generateReceiptNumber()

      // 1. Create the sale record (attach pending buyer if scanned before sale)
      const saleInsert: Record<string, any> = {
          flea_market_id: storeId,
          cashier_id: user?.id || null,
          subtotal: saleTotal,
          discount_amount: 0,
          total: saleTotal,
          receipt_number: receiptNumber,
      }
      if (pendingBuyerId) {
        saleInsert.buyer_id = pendingBuyerId
      }
      const { data: sale, error: saleError } = await supabase
        .from('sales')
        .insert(saleInsert)
        .select('id')
        .single()

      if (saleError) throw saleError

      // 2. Insert sale_items (one per cart item, snapshot price + quantity + seller)
      const saleItems = cart.map(item => ({
        sale_id: sale.id,
        product_id: item.product.id,
        price: item.product.price,
        quantity: item.quantity,
        seller_id: item.product.seller_id || user?.id,
        commission_percentage: 0,
        commission_amount: 0,
        seller_amount: item.product.price * item.quantity }))

      const { error: itemsError } = await supabase
        .from('sale_items')
        .insert(saleItems)

      if (itemsError) throw itemsError

      // 3. Record payment method
      const { error: paymentError } = await supabase
        .from('sale_payments')
        .insert({
          sale_id: sale.id,
          payment_method: paymentMethod,
          amount: saleTotal,
          status: 'completed' })

      if (paymentError) throw paymentError

      // 4. Mark products as sold
      for (const item of cart) {
        await supabase
          .from('products')
          .update({ status: 'sold', sold_at: new Date().toISOString() })
          .eq('id', item.product.id)
      }

      // Snapshot cart before clearing (needed for receipt display)
      const soldCart = [...cart]
      await clearCart(sale.id)
      setCashReceived('')

      if (session) {
        await endSession()
      }

      // Show receipt flow
      setReceiptCart(soldCart)
      setLastSaleId(sale.id)
      setLastSaleTotal(saleTotal)
      setLastSaleItems(saleItemCount)
      setReceiptStep('customer')
      setCustomerEmail('')
      setShowReceipt(true)
    } catch (error: any) {
      console.error('Payment error:', error)
      showAlert(t('error'), error.message || 'Failed to process payment')
    } finally {
      setProcessing(false)
    }
  }

  const handleCustomerEmail = async () => {
    if (!customerEmail.trim() || !customerEmail.includes('@')) {
      showAlert(t('error'), t('enter_valid_email') || 'Enter a valid email address')
      return
    }
    setEmailLooking(true)
    try {
      const email = customerEmail.trim().toLowerCase()

      // Check if this email belongs to an existing user
      const { data: existingUser } = await supabase
        .from('users')
        .select('id, email')
        .eq('email', email)
        .maybeSingle()

      // Update the sale with buyer info
      if (lastSaleId) {
        const updateData: Record<string, any> = { buyer_email: email }
        if (existingUser) {
          updateData.buyer_id = existingUser.id
        }
        await supabase
          .from('sales')
          .update(updateData)
          .eq('id', lastSaleId)
      }

      if (existingUser) {
        toast.success(
          t('customer_found') || 'Customer found',
          t('receipt_in_app') || 'Receipt visible in their app'
        )
      } else {
        toast.success(
          t('email_saved') || 'Email saved',
          t('receipt_emailed') || 'Receipt will be emailed'
        )
      }
      finishReceiptFlow(existingUser?.id ?? null, existingUser?.display_name ?? null, email)
    } catch (error: any) {
      console.error('Email lookup error:', error)
      toast.error(t('error') || 'Error', error.message)
    } finally {
      setEmailLooking(false)
    }
  }

  const skipCustomerID = () => {
    finishReceiptFlow()
  }

  const finishReceiptFlow = (buyerId?: string | null, buyerName?: string | null, buyerEmail?: string | null) => {
    // Persist the completed sale so the transaction bar can show it
    const soldReceiptNumber = generateReceiptNumber()
    setCompletedSale({
      id: lastSaleId!,
      total: lastSaleTotal,
      itemCount: lastSaleItems,
      receiptNumber: soldReceiptNumber,
      cart: receiptCart,
      buyerId: buyerId ?? pendingBuyerId,
      buyerName: buyerName ?? pendingBuyerName,
      buyerEmail: buyerEmail ?? null,
      completedAt: new Date(),
    })
    setShowReceipt(false)
    setCustomerEmail('')
    setLastSaleId(null)
    setPendingBuyerId(null)
    setPendingBuyerName(null)
    setScanningCustomerQR(false)
    toast.success(
      t('sale_completed') || 'Sale completed',
      `${lastSaleItems} ${lastSaleItems === 1 ? (t('item') || 'item') : (t('items') || 'items')} · ${lastSaleTotal.toFixed(2)} €`
    )
    searchInputRef.current?.focus()
  }

  const printReceipt = (sale: CompletedSale) => {
    if (typeof window === 'undefined') return
    const rows = sale.cart.map(item => {
      const title = (language === 'fi' ? item.product.title_fi : null) || item.product.title
      const qty = item.quantity > 1 ? ` ×${item.quantity}` : ''
      const price = (item.product.price * item.quantity).toFixed(2)
      return `<tr><td style="padding:4px 8px 4px 0;font-size:13px">${title}${qty}</td><td style="text-align:right;padding:4px 0 4px 8px;font-size:13px">${price} €</td></tr>`
    }).join('')
    const buyer = sale.buyerName ? `<p style="font-size:12px;color:#666;margin:4px 0">Asiakas: ${sale.buyerName}</p>` : ''
    const html = `<!DOCTYPE html><html><head><title>Kuitti ${sale.receiptNumber}</title>
    <style>body{font-family:monospace;max-width:300px;margin:0 auto;padding:16px}@media print{body{max-width:100%}}</style></head>
    <body>
    <h2 style="text-align:center;font-size:16px;margin-bottom:4px">Fliisi</h2>
    <p style="text-align:center;font-size:11px;color:#666;margin:0">${sale.receiptNumber}</p>
    <p style="text-align:center;font-size:11px;color:#666;margin:2px 0 12px">${sale.completedAt.toLocaleString(language === 'fi' ? 'fi-FI' : 'en-US')}</p>
    <hr style="border:none;border-top:1px dashed #ccc"/>
    <table style="width:100%;border-collapse:collapse">${rows}</table>
    <hr style="border:none;border-top:1px dashed #ccc"/>
    <p style="text-align:right;font-weight:700;font-size:15px">Yht. ${sale.total.toFixed(2)} €</p>
    ${buyer}
    </body></html>`
    const w = window.open('', '_blank', 'width=350,height=500')
    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print() }
  }

  const voidCompletedSale = async (sale: CompletedSale) => {
    try {
      await supabase.from('sales').update({ voided_at: new Date().toISOString(), void_reason: 'Cashier void' }).eq('id', sale.id)
      for (const item of sale.cart) {
        await supabase.from('products').update({ status: 'for_sale', sold_at: null }).eq('id', item.product.id)
      }
      setCompletedSale(null)
      setShowTransactionDetail(false)
      toast.success(t('transaction_voided') || 'Myynti peruttu', '')
    } catch (e: any) {
      toast.error(t('error') || 'Virhe', e.message)
    }
  }

  const linkCustomerToCompletedSale = async () => {
    if (!completedSale || !txEmail.trim()) return
    setTxEmailLooking(true)
    try {
      const email = txEmail.trim().toLowerCase()
      const { data: existingUser } = await supabase.from('users').select('id, display_name, email').eq('email', email).maybeSingle()
      const updateData: Record<string, any> = { buyer_email: email }
      if (existingUser) updateData.buyer_id = existingUser.id
      await supabase.from('sales').update(updateData).eq('id', completedSale.id)
      setCompletedSale(prev => prev ? { ...prev, buyerEmail: email, buyerId: existingUser?.id ?? prev.buyerId, buyerName: existingUser?.display_name ?? prev.buyerName } : null)
      setTxEmail('')
      setTxScanningQR(false)
      toast.success(existingUser ? (t('customer_found') || 'Asiakas löytyi') : (t('email_saved') || 'Sähköposti tallennettu'), '')
    } catch (e: any) {
      toast.error(t('error') || 'Virhe', e.message)
    } finally {
      setTxEmailLooking(false)
    }
  }

  const getProductTitle = (product: Product) => {
    if (language === 'fi' && product.title_fi) return product.title_fi
    return product.title
  }

  const cashChange = parseFloat(cashReceived) - cartTotal

  // Scanner mode view (phone with camera)
  if (mode === 'scanner') {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={endSession}>
            <Unlink size={20} color={merchantColors.primary} />
          </TouchableOpacity>
          <View style={styles.headerCenter}>
            <Text style={styles.headerTitle}>{t('scanner_mode') || 'Scanner Mode'}</Text>
            <Text style={styles.sessionCode}>{session?.session_code}</Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.cartBadge}>{cartItemCount}</Text>
          </View>
        </View>

        {permission?.granted ? (
          <View style={styles.cameraContainer}>
            <CameraView
              style={styles.camera}
              facing="back"
              barcodeScannerSettings={{
                barcodeTypes: ['ean13', 'ean8', 'upc_a', 'upc_e', 'code128', 'code39', 'qr'] }}
              onBarcodeScanned={scanned ? undefined : handleBarcodeScanned}
            />
            <View style={styles.scanOverlay}>
              <View style={styles.scanFrame} />
              <Text style={styles.scanHint}>
                {scanned
                  ? (t('item_added') || 'Item added!')
                  : (t('scan_barcode') || 'Scan barcode')}
              </Text>
            </View>
          </View>
        ) : (
          <View style={styles.permissionContainer}>
            <Camera size={48} color={theme.colors.text.secondary} />
            <Text style={styles.permissionText}>
              {t('camera_permission_needed') || 'Camera permission is needed to scan barcodes'}
            </Text>
            <TouchableOpacity style={styles.permissionButton} onPress={requestPermission}>
              <Text style={styles.permissionButtonText}>
                {t('grant_permission') || 'Grant Permission'}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Mini cart preview */}
        <View style={styles.miniCart}>
          <ShoppingCart size={20} color={merchantColors.primary} />
          <Text style={styles.miniCartText}>
            {cartItemCount} {t('items') || 'items'} · {cartTotal.toFixed(2)} €
          </Text>
          {cart.length > 0 && (
            <Text style={styles.lastItemText} numberOfLines={1}>
              {t('last') || 'Last'}: {getProductTitle(cart[cart.length - 1].product)}
            </Text>
          )}
        </View>
      </View>
    )
  }

  // Register / Standalone mode view (desktop with cart)
  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.navigate('MerchantDashboard', { storeId })}
        >
          <ArrowLeft size={20} color={merchantColors.primary} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('point_of_sale') || 'Point of Sale'}</Text>
        <View style={styles.headerRight}>
          {session ? (
            <TouchableOpacity style={styles.sessionBadge} onPress={copySessionCode}>
              <Link2 size={16} color={merchantColors.accent} />
              <Text style={styles.sessionBadgeText}>{session.session_code}</Text>
              <Copy size={14} color={merchantColors.accent} />
            </TouchableOpacity>
          ) : (
            <TouchableOpacity style={styles.modeButton} onPress={() => setShowModeModal(true)}>
              <Monitor size={18} color={merchantColors.primary} />
              <Text style={styles.modeButtonText}>{t('multi_device') || 'Multi-device'}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Connected indicator */}
      {session && (
        <View style={styles.connectedBanner}>
          <Link2 size={16} color="#fff" />
          <Text style={styles.connectedText}>
            {t('register_mode') || 'Register mode'} · {t('waiting_for_scans') || 'Waiting for scans'}
          </Text>
          <TouchableOpacity onPress={endSession}>
            <X size={20} color="#fff" />
          </TouchableOpacity>
        </View>
      )}

      <View style={[styles.content, isNarrow && styles.contentNarrow]}>
        {/* Left side - Search and Products */}
        <View style={[styles.searchSection, isNarrow && activeTab !== 'search' && styles.hidden]}>
          <View style={styles.searchRow}>
            <View style={styles.searchBar}>
              <ScanBarcode size={20} color={theme.colors.text.secondary} />
              <TextInput
                ref={searchInputRef}
                style={styles.searchInput}
                value={searchQuery}
                onChangeText={handleSearchChange}
                placeholder={t('scan_or_search') || 'Scan barcode or search products...'}
                placeholderTextColor={theme.colors.text.secondary}
                autoFocus={!session}
              />
              {searchQuery ? (
                <TouchableOpacity onPress={() => { setSearchQuery(''); setSearchResults([]) }}>
                  <X size={20} color={theme.colors.text.secondary} />
                </TouchableOpacity>
              ) : null}
              {searching && <Spinner size="small" />}
            </View>
            <TouchableOpacity style={styles.browseButton} onPress={openInventoryPicker}>
              <Package size={18} color={merchantColors.primary} />
              <Text style={styles.browseButtonText}>{t('browse') || 'Browse'}</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.searchResults}>
            {searchResults.length > 0 ? (
              searchResults.map(product => (
                <TouchableOpacity
                  key={product.id}
                  style={styles.productResult}
                  onPress={() => {
                    addToCart(product)
                    setSearchQuery('')
                    setSearchResults([])
                  }}
                >
                  {product.image_url ? (
                    <Image source={{ uri: product.image_url }} style={styles.productImage} />
                  ) : (
                    <View style={[styles.productImage, styles.noImage]}>
                      <ShoppingCart size={20} color={theme.colors.text.secondary} />
                    </View>
                  )}
                  <View style={styles.productInfo}>
                    <Text style={styles.productTitle} numberOfLines={1}>
                      {getProductTitle(product)}
                    </Text>
                    {product.barcode && (
                      <Text style={styles.productBarcode}>{product.barcode}</Text>
                    )}
                  </View>
                  <Text style={styles.productPrice}>{product.price.toFixed(2)} €</Text>
                  <Plus size={20} color={merchantColors.primary} />
                </TouchableOpacity>
              ))
            ) : searchQuery && !searching ? (
              <View style={styles.noResults}>
                <Text style={styles.noResultsText}>{t('no_products_found') || 'No products found'}</Text>
              </View>
            ) : !searchQuery ? (
              <View style={styles.searchHint}>
                <ScanBarcode size={48} color={theme.colors.text.secondary} />
                <Text style={styles.searchHintTitle}>{t('ready_to_scan') || 'Ready to scan'}</Text>
                <Text style={styles.searchHintText}>
                  {session
                    ? (t('scanner_will_add') || 'Items scanned on connected device will appear here')
                    : (t('scan_barcode_hint') || 'Scan a barcode or type to search for products')}
                </Text>
              </View>
            ) : null}
          </ScrollView>
        </View>

        {/* Right side - Cart */}
        <View style={[styles.cartSection, isNarrow && { flex: 1, width: undefined }, isNarrow && activeTab !== 'cart' && styles.hidden]}>
          <View style={styles.cartHeader}>
            <ShoppingCart size={20} color={merchantColors.primary} />
            <Text style={styles.cartTitle}>{t('cart') || 'Cart'}</Text>
            <Text style={styles.cartCount}>({cartItemCount})</Text>
            {cart.length > 0 && (
              <TouchableOpacity onPress={clearCart} style={styles.clearCart}>
                <Trash2 size={16} color={theme.colors.status.error} />
              </TouchableOpacity>
            )}
          </View>

          <ScrollView style={styles.cartItems}>
            {cart.length === 0 ? (
              <View style={styles.emptyCart}>
                <Text style={styles.emptyCartText}>{t('cart_empty') || 'Cart is empty'}</Text>
              </View>
            ) : (
              cart.map(item => (
                <View key={item.product.id} style={styles.cartItem}>
                  <View style={styles.cartItemInfo}>
                    <Text style={styles.cartItemTitle} numberOfLines={1}>
                      {getProductTitle(item.product)}
                    </Text>
                    <Text style={styles.cartItemPrice}>{item.product.price.toFixed(2)} €</Text>
                  </View>
                  <View style={styles.quantityControls}>
                    <TouchableOpacity
                      style={styles.qtyButton}
                      onPress={() => updateQuantity(item.product.id, -1)}
                    >
                      <Minus size={16} color={merchantColors.primary} />
                    </TouchableOpacity>
                    <Text style={styles.qtyText}>{item.quantity}</Text>
                    <TouchableOpacity
                      style={styles.qtyButton}
                      onPress={() => updateQuantity(item.product.id, 1)}
                    >
                      <Plus size={16} color={merchantColors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.removeButton}
                      onPress={() => removeFromCart(item.product.id)}
                    >
                      <X size={16} color={theme.colors.status.error} />
                    </TouchableOpacity>
                  </View>
                  <Text style={styles.cartItemTotal}>
                    {(item.product.price * item.quantity).toFixed(2)} €
                  </Text>
                </View>
              ))
            )}
          </ScrollView>

          {/* Cart Footer */}
          <View style={styles.cartFooter}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>{t('total') || 'Total'}</Text>
              <Text style={styles.totalAmount}>{cartTotal.toFixed(2)} €</Text>
            </View>

            {/* Pending customer indicator */}
            {pendingBuyerId && (
              <TouchableOpacity
                style={{
                  flexDirection: 'row', alignItems: 'center', gap: 8,
                  backgroundColor: `${merchantColors.accent}15`,
                  borderRadius: theme.borderRadius.md,
                  padding: 10, marginBottom: 8,
                }}
                onPress={() => { setPendingBuyerId(null); setPendingBuyerName(null) }}
              >
                <UserCheck size={16} color={merchantColors.accent} />
                <Text style={{ flex: 1, fontSize: 13, fontWeight: '600', color: merchantColors.accent }}>
                  {pendingBuyerName}
                </Text>
                <X size={14} color={merchantColors.accent} />
              </TouchableOpacity>
            )}

            <View style={styles.paymentMethods}>
              {availablePaymentMethods.map(method => {
                const isActive = paymentMethod === method
                const Icon = method === 'cash' ? Banknote : method === 'card' ? CreditCard : Smartphone
                const label = method === 'cash' ? (t('cash') || 'Cash')
                  : method === 'card' ? (t('card') || 'Card')
                  : 'MobilePay'
                return (
                  <TouchableOpacity
                    key={method}
                    style={[styles.paymentOption, isActive && styles.paymentOptionActive]}
                    onPress={() => setPaymentMethod(method)}
                  >
                    <Icon size={18} color={isActive ? '#fff' : merchantColors.primary} />
                    <Text style={[styles.paymentText, isActive && styles.paymentTextActive]}>
                      {label}
                    </Text>
                  </TouchableOpacity>
                )
              })}
            </View>

            {paymentMethod === 'cash' && cart.length > 0 && (
              <View style={styles.cashSection}>
                <View style={styles.cashInputRow}>
                  <Text style={styles.cashLabel}>{t('cash_received') || 'Cash received'}</Text>
                  <TextInput
                    style={styles.cashInput}
                    value={cashReceived}
                    onChangeText={setCashReceived}
                    keyboardType="decimal-pad"
                    placeholder="0.00"
                    placeholderTextColor={theme.colors.text.secondary}
                  />
                  <Text style={styles.cashCurrency}>€</Text>
                </View>
                {cashReceived && cashChange >= 0 && (
                  <View style={styles.changeRow}>
                    <Text style={styles.changeLabel}>{t('change') || 'Change'}</Text>
                    <Text style={styles.changeAmount}>{cashChange.toFixed(2)} €</Text>
                  </View>
                )}
              </View>
            )}

            <TouchableOpacity
              style={[styles.completeButton, cart.length === 0 && styles.completeButtonDisabled]}
              onPress={processPayment}
              disabled={cart.length === 0 || processing}
            >
              {processing ? (
                <Spinner color="#fff" />
              ) : (
                <>
                  <Check size={20} color="#fff" />
                  <Text style={styles.completeButtonText}>
                    {t('complete_sale') || 'Complete Sale'} - {cartTotal.toFixed(2)} €
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Narrow screen tab bar */}
      {isNarrow && (
        <View style={styles.tabBar}>
          <TouchableOpacity
            style={[styles.tabBtn, activeTab === 'search' && styles.tabBtnActive]}
            onPress={() => setActiveTab('search')}
          >
            <Search size={18} color={activeTab === 'search' ? merchantColors.primary : '#999'} />
            <Text style={[styles.tabBtnText, activeTab === 'search' && styles.tabBtnTextActive]}>
              {t('products') || 'Products'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tabBtn, activeTab === 'cart' && styles.tabBtnActive]}
            onPress={() => setActiveTab('cart')}
          >
            <ShoppingCart size={18} color={activeTab === 'cart' ? merchantColors.primary : '#999'} />
            <Text style={[styles.tabBtnText, activeTab === 'cart' && styles.tabBtnTextActive]}>
              {t('cart') || 'Cart'}
              {cartItemCount > 0 && (
                <Text style={styles.tabCartBadge}> ({cartItemCount})</Text>
              )}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Inventory Browse Modal */}
      <Modal
        visible={showInventory}
        transparent
        animationType="slide"
        onRequestClose={() => setShowInventory(false)}
      >
        <View style={styles.inventoryOverlay}>
          <View style={styles.inventoryModal}>
            <View style={styles.inventoryHeader}>
              <Text style={styles.inventoryTitle}>{t('select_product') || 'Select product'}</Text>
              <TouchableOpacity onPress={() => setShowInventory(false)}>
                <X size={22} color={theme.colors.text.secondary} />
              </TouchableOpacity>
            </View>
            {inventoryLoading ? (
              <View style={styles.inventoryLoading}>
                <Spinner size="large" />
              </View>
            ) : (
              <ScrollView style={styles.inventoryList}>
                {inventoryProducts.length === 0 ? (
                  <View style={styles.inventoryEmpty}>
                    <Text style={styles.inventoryEmptyText}>{t('no_products_for_sale') || 'No products for sale'}</Text>
                  </View>
                ) : (
                  inventoryProducts.map(product => {
                    const alreadyInCart = cart.some(i => i.product.id === product.id)
                    return (
                      <TouchableOpacity
                        key={product.id}
                        style={[styles.inventoryItem, alreadyInCart && styles.inventoryItemInCart]}
                        onPress={() => {
                          addToCart(product)
                          setShowInventory(false)
                        }}
                      >
                        {product.image_url ? (
                          <Image source={{ uri: product.image_url }} style={styles.inventoryThumb} />
                        ) : (
                          <View style={[styles.inventoryThumb, styles.noImage]}>
                            <Package size={16} color="#ccc" />
                          </View>
                        )}
                        <View style={styles.inventoryItemInfo}>
                          <Text style={styles.inventoryItemTitle} numberOfLines={1}>
                            {getProductTitle(product)}
                          </Text>
                          {product.barcode && (
                            <Text style={styles.inventoryItemBarcode}>{product.barcode}</Text>
                          )}
                        </View>
                        <Text style={styles.inventoryItemPrice}>{product.price.toFixed(2)} €</Text>
                        {alreadyInCart ? (
                          <Check size={18} color={merchantColors.accent} />
                        ) : (
                          <Plus size={18} color={merchantColors.primary} />
                        )}
                      </TouchableOpacity>
                    )
                  })
                )}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Last Transaction Bar */}
      {completedSale && !showReceipt && (
        <TouchableOpacity
          style={styles.txBar}
          onPress={() => { setTxEmail(''); setTxScanningQR(false); setShowTransactionDetail(true) }}
          activeOpacity={0.85}
        >
          <View style={styles.txBarLeft}>
            <Receipt size={16} color={merchantColors.primary} />
            <Text style={styles.txBarTitle}>{t('last_transaction') || 'Viimeisin myynti'}</Text>
            {completedSale.buyerName ? (
              <View style={styles.txBuyerChip}>
                <UserCheck size={11} color={merchantColors.accent} />
                <Text style={styles.txBuyerChipText}>{completedSale.buyerName}</Text>
              </View>
            ) : null}
          </View>
          <View style={styles.txBarRight}>
            <Text style={styles.txBarAmount}>{completedSale.total.toFixed(2)} €</Text>
            <Text style={styles.txBarItems}>{completedSale.itemCount} {completedSale.itemCount === 1 ? (t('item') || 'tuote') : (t('items') || 'tuotetta')}</Text>
          </View>
        </TouchableOpacity>
      )}

      {/* Transaction Detail Modal */}
      <Modal
        visible={showTransactionDetail}
        transparent
        animationType="slide"
        onRequestClose={() => setShowTransactionDetail(false)}
      >
        {completedSale && (
          <View style={styles.receiptOverlay}>
            <View style={[styles.receiptModal, { maxHeight: '90%' }]}>
              {/* Header */}
              <View style={[styles.receiptSuccess, { paddingBottom: 12 }]}>
                <Text style={styles.receiptSuccessTitle}>{t('transaction') || 'Myynti'} · {completedSale.receiptNumber}</Text>
                <Text style={styles.receiptSuccessAmount}>{completedSale.total.toFixed(2)} €</Text>
                <Text style={styles.receiptSuccessItems}>
                  {completedSale.completedAt.toLocaleString(language === 'fi' ? 'fi-FI' : 'en-US')}
                </Text>
              </View>

              <ScrollView style={{ maxHeight: 220 }}>
                {/* Items */}
                <View style={styles.receiptItems}>
                  {completedSale.cart.map((item, i) => (
                    <View key={item.product.id} style={[styles.receiptItemRow, i > 0 && styles.receiptItemRowBorder]}>
                      <Text style={styles.receiptItemName} numberOfLines={1}>
                        {(language === 'fi' ? item.product.title_fi : null) || item.product.title}
                        {item.quantity > 1 ? ` ×${item.quantity}` : ''}
                      </Text>
                      <Text style={styles.receiptItemPrice}>{(item.product.price * item.quantity).toFixed(2)} €</Text>
                    </View>
                  ))}
                </View>

                {/* Linked customer */}
                <View style={styles.receiptSection}>
                  <Text style={styles.receiptSectionTitle}>{t('customer') || 'Asiakas'}</Text>
                  {completedSale.buyerName || completedSale.buyerEmail ? (
                    <View style={[styles.receiptOption, { backgroundColor: `${merchantColors.accent}15` }]}>
                      <UserCheck size={20} color={merchantColors.accent} />
                      <View style={styles.receiptOptionInfo}>
                        <Text style={[styles.receiptOptionTitle, { color: merchantColors.accent }]}>
                          {completedSale.buyerName || completedSale.buyerEmail}
                        </Text>
                        {completedSale.buyerEmail && completedSale.buyerName && (
                          <Text style={styles.receiptOptionHint}>{completedSale.buyerEmail}</Text>
                        )}
                      </View>
                    </View>
                  ) : (
                    <>
                      <Text style={styles.receiptSectionHint}>{t('customer_hint') || 'Identify the customer for a digital receipt'}</Text>
                      <View style={styles.receiptEmailRow}>
                        <Mail size={18} color={merchantColors.primary} />
                        <TextInput
                          style={styles.receiptEmailInput}
                          value={txEmail}
                          onChangeText={setTxEmail}
                          placeholder={t('customer_email') || 'Customer email'}
                          placeholderTextColor="#aaa"
                          keyboardType="email-address"
                          autoCapitalize="none"
                          autoCorrect={false}
                          onSubmitEditing={linkCustomerToCompletedSale}
                        />
                        <TouchableOpacity
                          style={[styles.receiptEmailSend, (!txEmail.trim() || txEmailLooking) && styles.receiptEmailSendDisabled]}
                          onPress={linkCustomerToCompletedSale}
                          disabled={!txEmail.trim() || txEmailLooking}
                        >
                          {txEmailLooking ? <Spinner size="small" /> : <Check size={18} color="#fff" />}
                        </TouchableOpacity>
                      </View>

                      {txScanningQR ? (
                        <View style={styles.receiptQRScanner}>
                          {permission?.granted ? (
                            <>
                              <CameraView
                                style={styles.receiptQRCamera}
                                barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
                                onBarcodeScanned={scanned ? undefined : handleBarcodeScanned}
                              />
                              <TouchableOpacity style={styles.receiptQRClose} onPress={() => { setTxScanningQR(false); setScanned(false) }}>
                                <X size={20} color="#fff" />
                              </TouchableOpacity>
                            </>
                          ) : (
                            <TouchableOpacity style={styles.receiptQRPermissionBtn} onPress={requestPermission}>
                              <Text style={{ color: '#fff', fontWeight: '600' }}>{t('grant_permission') || 'Anna lupa'}</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      ) : (
                        <TouchableOpacity style={styles.receiptOption} onPress={async () => { if (!permission?.granted) await requestPermission(); setTxScanningQR(true); setScanned(false) }}>
                          <QrCode size={20} color={merchantColors.primary} />
                          <View style={styles.receiptOptionInfo}>
                            <Text style={styles.receiptOptionTitle}>{t('scan_customer_qr') || 'Skannaa asiakkaan QR'}</Text>
                            <Text style={styles.receiptOptionHint}>{t('customer_shows_qr') || 'Asiakas näyttää QR-koodin Fliisi-sovelluksesta'}</Text>
                          </View>
                        </TouchableOpacity>
                      )}
                    </>
                  )}
                </View>
              </ScrollView>

              {/* Actions */}
              <View style={[styles.receiptActions, { flexDirection: 'row', justifyContent: 'space-between', gap: 8 }]}>
                <TouchableOpacity
                  style={[styles.receiptSkipBtn, { backgroundColor: `${merchantColors.primary}15`, borderRadius: theme.borderRadius.md, flex: 1, justifyContent: 'center' }]}
                  onPress={() => printReceipt(completedSale)}
                >
                  <Printer size={18} color={merchantColors.primary} />
                  <Text style={[styles.receiptSkipText, { color: merchantColors.primary }]}>{t('print') || 'Tulosta'}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.receiptSkipBtn, { backgroundColor: '#EF444415', borderRadius: theme.borderRadius.md, flex: 1, justifyContent: 'center' }]}
                  onPress={() => voidCompletedSale(completedSale)}
                >
                  <X size={18} color="#EF4444" />
                  <Text style={[styles.receiptSkipText, { color: '#EF4444' }]}>{t('void') || 'Peruuta myynti'}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.receiptSkipBtn, { flex: 0.6, justifyContent: 'center' }]}
                  onPress={() => setShowTransactionDetail(false)}
                >
                  <Text style={styles.receiptSkipText}>{t('close') || 'Sulje'}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}
      </Modal>

      {/* Receipt / Customer ID Modal */}
      <Modal
        visible={showReceipt}
        transparent
        animationType="fade"
        onRequestClose={skipCustomerID}
      >
        <View style={styles.receiptOverlay}>
          <View style={styles.receiptModal}>
            {/* Success header */}
            <View style={styles.receiptSuccess}>
              <View style={styles.receiptCheckCircle}>
                <CheckCircle size={40} color={merchantColors.accent} />
              </View>
              <Text style={styles.receiptSuccessTitle}>{t('payment_received') || 'Payment received'}</Text>
              <Text style={styles.receiptSuccessAmount}>{lastSaleTotal.toFixed(2)} €</Text>
              <Text style={styles.receiptSuccessItems}>
                {lastSaleItems} {lastSaleItems === 1 ? (t('item') || 'item') : (t('items') || 'items')}
              </Text>
            </View>

            {/* Itemized list */}
            <View style={styles.receiptItems}>
              {receiptCart.map((item, i) => (
                <View key={item.product.id} style={[styles.receiptItemRow, i > 0 && styles.receiptItemRowBorder]}>
                  <Text style={styles.receiptItemName} numberOfLines={1}>
                    {(language === 'fi' ? item.product.title_fi : null) || item.product.title}
                    {item.quantity > 1 ? ` ×${item.quantity}` : ''}
                  </Text>
                  <Text style={styles.receiptItemPrice}>{(item.product.price * item.quantity).toFixed(2)} €</Text>
                </View>
              ))}
            </View>

            {/* Customer identification */}
            <View style={styles.receiptSection}>
              <Text style={styles.receiptSectionTitle}>
                {t('customer_optional') || 'Customer (optional)'}
              </Text>
              <Text style={styles.receiptSectionHint}>
                {t('customer_hint') || 'Identify the customer for a digital receipt'}
              </Text>

              {/* Email input */}
              <View style={styles.receiptEmailRow}>
                <Mail size={18} color={merchantColors.primary} />
                <TextInput
                  style={styles.receiptEmailInput}
                  value={customerEmail}
                  onChangeText={setCustomerEmail}
                  placeholder={t('customer_email') || 'Customer email'}
                  placeholderTextColor="#aaa"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  onSubmitEditing={handleCustomerEmail}
                />
                <TouchableOpacity
                  style={[
                    styles.receiptEmailSend,
                    (!customerEmail.trim() || emailLooking) && styles.receiptEmailSendDisabled,
                  ]}
                  onPress={handleCustomerEmail}
                  disabled={!customerEmail.trim() || emailLooking}
                >
                  {emailLooking ? (
                    <Spinner size="small" />
                  ) : (
                    <Check size={18} color="#fff" />
                  )}
                </TouchableOpacity>
              </View>

              {/* QR scan option */}
              {scanningCustomerQR ? (
                <View style={styles.receiptQRScanner}>
                  {permission?.granted ? (
                    <>
                      <CameraView
                        style={styles.receiptQRCamera}
                        barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
                        onBarcodeScanned={scanned ? undefined : handleBarcodeScanned}
                      />
                      <TouchableOpacity
                        style={styles.receiptQRClose}
                        onPress={() => setScanningCustomerQR(false)}
                      >
                        <X size={20} color="#fff" />
                      </TouchableOpacity>
                    </>
                  ) : (
                    <View style={styles.receiptQRPermission}>
                      <Text style={styles.receiptOptionHint}>
                        {t('camera_permission_needed') || 'Kameralupa tarvitaan QR-skannaukseen'}
                      </Text>
                      <TouchableOpacity
                        style={styles.receiptQRPermissionBtn}
                        onPress={requestPermission}
                      >
                        <Text style={{ color: '#fff', fontWeight: '600' }}>
                          {t('grant_permission') || 'Anna lupa'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              ) : (
                <TouchableOpacity
                  style={styles.receiptOption}
                  onPress={async () => {
                    if (!permission?.granted) await requestPermission()
                    setScanningCustomerQR(true)
                    setScanned(false)
                  }}
                >
                  <QrCode size={20} color={merchantColors.primary} />
                  <View style={styles.receiptOptionInfo}>
                    <Text style={styles.receiptOptionTitle}>
                      {t('scan_customer_qr') || 'Skannaa asiakkaan QR'}
                    </Text>
                    <Text style={styles.receiptOptionHint}>
                      {t('customer_shows_qr') || 'Asiakas näyttää QR-koodin Fliisi-sovelluksesta'}
                    </Text>
                  </View>
                </TouchableOpacity>
              )}

              {/* Show pending buyer if already identified */}
              {pendingBuyerId && (
                <View style={[styles.receiptOption, { backgroundColor: `${merchantColors.accent}15` }]}>
                  <UserCheck size={20} color={merchantColors.accent} />
                  <View style={styles.receiptOptionInfo}>
                    <Text style={[styles.receiptOptionTitle, { color: merchantColors.accent }]}>
                      {pendingBuyerName || t('customer_identified') || 'Asiakas tunnistettu'}
                    </Text>
                    <Text style={styles.receiptOptionHint}>
                      {t('receipt_linked') || 'Kuitti liitetty asiakkaan profiiliin'}
                    </Text>
                  </View>
                </View>
              )}
            </View>

            {/* Actions */}
            <View style={styles.receiptActions}>
              <TouchableOpacity style={styles.receiptSkipBtn} onPress={skipCustomerID}>
                <SkipForward size={18} color={theme.colors.text.secondary} />
                <Text style={styles.receiptSkipText}>{t('skip') || 'Skip'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Mode Selection Modal */}
      <Modal
        visible={showModeModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowModeModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowModeModal(false)}
        >
          <View
            style={styles.modalContent}
            onStartShouldSetResponder={() => true}
            // @ts-ignore – web: stop click from bubbling to backdrop
            onClick={(e: any) => e.stopPropagation()}
          >
            <Text style={styles.modalTitle}>{t('multi_device_pos') || 'Multi-device POS'}</Text>
            <Text style={styles.modalSubtitle}>
              {t('multi_device_desc') || 'Use your phone as a barcode scanner while this screen shows the cart'}
            </Text>

            {/* Start as Register */}
            <TouchableOpacity
              style={styles.modeOption}
              onPress={createSession}
              disabled={connecting}
            >
              <View style={styles.modeIconContainer}>
                <Monitor size={32} color={merchantColors.primary} />
              </View>
              <View style={styles.modeOptionInfo}>
                <Text style={styles.modeOptionTitle}>
                  {t('start_register') || 'Start as Register'}
                </Text>
                <Text style={styles.modeOptionDesc}>
                  {t('register_desc') || 'Generate a code for phone to connect'}
                </Text>
              </View>
              {connecting && <Spinner size="small" />}
            </TouchableOpacity>

            {/* Join as Scanner */}
            <View style={styles.modeOption}>
              <View style={styles.modeIconContainer}>
                <Camera size={32} color={merchantColors.primary} />
              </View>
              <View style={styles.modeOptionInfo}>
                <Text style={styles.modeOptionTitle}>
                  {t('join_as_scanner') || 'Join as Scanner'}
                </Text>
                <Text style={styles.modeOptionDesc}>
                  {t('scanner_desc') || 'Enter code from register device'}
                </Text>
                <View style={styles.joinCodeRow}>
                  <TextInput
                    style={styles.joinCodeInput}
                    value={joinCode}
                    onChangeText={(text) => setJoinCode(text.toUpperCase())}
                    placeholder="ABC123"
                    placeholderTextColor={theme.colors.text.secondary}
                    maxLength={6}
                    autoCapitalize="characters"
                  />
                  <TouchableOpacity
                    style={styles.joinButton}
                    onPress={joinSession}
                    disabled={connecting || joinCode.length !== 6}
                  >
                    <Text style={styles.joinButtonText}>{t('join') || 'Join'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            <TouchableOpacity style={styles.cancelButton} onPress={() => setShowModeModal(false)}>
              <Text style={styles.cancelButtonText}>{t('cancel') || 'Cancel'}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border },
  headerCenter: {
    alignItems: 'center' },
  backButton: {
    padding: 8 },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  headerRight: {
    alignItems: 'flex-end' },
  storeName: {
    fontSize: 12,
    color: theme.colors.text.secondary },
  modeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: merchantColors.primary },
  modeButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: merchantColors.primary },
  sessionBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: theme.borderRadius.md,
    backgroundColor: `${merchantColors.accent}20` },
  sessionBadgeText: {
    fontSize: 14,
    fontWeight: '600',
    color: merchantColors.accent,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace' },
  cartBadge: {
    fontSize: 24,
    fontWeight: 'bold',
    color: merchantColors.primary },
  sessionCode: {
    fontSize: 14,
    fontWeight: '600',
    color: merchantColors.accent,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace' },
  connectedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: theme.spacing.md,
    backgroundColor: merchantColors.accent },
  connectedText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: '#fff' },
  content: {
    flex: 1,
    flexDirection: 'row' },
  contentNarrow: {
    position: 'relative' },
  hidden: {
    display: 'none' },
  tabBar: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.border,
    backgroundColor: theme.colors.background.card },
  tabBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderTopWidth: 2,
    borderTopColor: 'transparent' },
  tabBtnActive: {
    borderTopColor: merchantColors.primary },
  tabBtnText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#999' },
  tabBtnTextActive: {
    color: merchantColors.primary },
  tabCartBadge: {
    fontWeight: '700',
    color: merchantColors.primary },
  // Search Section (left)
  searchSection: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: theme.colors.ui.border },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    gap: 10 },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text.primary },
  searchResults: {
    flex: 1,
    padding: theme.spacing.md,
    paddingTop: 0 },
  productResult: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.sm,
    gap: 12 },
  productImage: {
    width: 48,
    height: 48,
    borderRadius: 8 },
  noImage: {
    backgroundColor: theme.colors.background.primary,
    justifyContent: 'center',
    alignItems: 'center' },
  productInfo: {
    flex: 1 },
  productTitle: {
    fontSize: 15,
    fontWeight: '500',
    color: theme.colors.text.primary },
  productBarcode: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  productPrice: {
    fontSize: 16,
    fontWeight: '600',
    color: merchantColors.primary,
    marginRight: 8 },
  noResults: {
    padding: theme.spacing.xl,
    alignItems: 'center' },
  noResultsText: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  searchHint: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xl },
  searchHintTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginTop: theme.spacing.md },
  searchHintText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    textAlign: 'center',
    marginTop: theme.spacing.sm },
  // Cart Section (right)
  cartSection: {
    width: 360,
    backgroundColor: theme.colors.background.card,
    display: 'flex',
    flexDirection: 'column' },
  cartHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border,
    gap: 8 },
  cartTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary },
  cartCount: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    flex: 1 },
  clearCart: {
    padding: 8 },
  cartItems: {
    flex: 1 },
  emptyCart: {
    padding: theme.spacing.xl,
    alignItems: 'center' },
  emptyCartText: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  cartItem: {
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border },
  cartItemInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8 },
  cartItemTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary },
  cartItemPrice: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  quantityControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8 },
  qtyButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: `${merchantColors.primary}15`,
    justifyContent: 'center',
    alignItems: 'center' },
  qtyText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    minWidth: 24,
    textAlign: 'center' },
  removeButton: {
    padding: 8,
    marginLeft: 'auto' },
  cartItemTotal: {
    fontSize: 16,
    fontWeight: '600',
    color: merchantColors.primary,
    textAlign: 'right',
    marginTop: 4 },
  // Cart Footer
  cartFooter: {
    padding: theme.spacing.md,
    borderTopWidth: 2,
    borderTopColor: theme.colors.ui.border,
    backgroundColor: theme.colors.background.primary },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.md },
  totalLabel: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary },
  totalAmount: {
    fontSize: 28,
    fontWeight: 'bold',
    color: merchantColors.primary },
  paymentMethods: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: theme.spacing.md },
  paymentOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderRadius: theme.borderRadius.md,
    borderWidth: 2,
    borderColor: merchantColors.primary,
    backgroundColor: 'transparent' },
  paymentOptionActive: {
    backgroundColor: merchantColors.primary },
  paymentText: {
    fontSize: 13,
    fontWeight: '500',
    color: merchantColors.primary },
  paymentTextActive: {
    color: '#fff' },
  cashSection: {
    marginBottom: theme.spacing.md },
  cashInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8 },
  cashLabel: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  cashInput: {
    flex: 1,
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 12,
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'right',
    color: theme.colors.text.primary,
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  cashCurrency: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary },
  changeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    padding: 12,
    backgroundColor: `${merchantColors.accent}20`,
    borderRadius: theme.borderRadius.md },
  changeLabel: {
    fontSize: 14,
    color: merchantColors.accent,
    fontWeight: '500' },
  changeAmount: {
    fontSize: 20,
    fontWeight: 'bold',
    color: merchantColors.accent },
  completeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: merchantColors.primary,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md },
  completeButtonDisabled: {
    opacity: 0.5 },
  completeButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  // Scanner mode
  cameraContainer: {
    flex: 1,
    position: 'relative' },
  camera: {
    flex: 1 },
  scanOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center' },
  scanFrame: {
    width: 250,
    height: 150,
    borderWidth: 3,
    borderColor: merchantColors.primary,
    borderRadius: 8,
    backgroundColor: 'transparent' },
  scanHint: {
    marginTop: 20,
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
    textShadowColor: 'rgba(0,0,0,0.8)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3 },
  miniCart: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background.card,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.border },
  miniCartText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  lastItemText: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.text.secondary,
    textAlign: 'right' },
  permissionContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xl,
    gap: 16 },
  permissionText: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    textAlign: 'center' },
  permissionButton: {
    backgroundColor: merchantColors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: theme.borderRadius.md },
  permissionButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xl },
  modalContent: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.xl,
    width: '100%',
    maxWidth: 400 },
  modalTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: theme.colors.text.primary,
    marginBottom: 8 },
  modalSubtitle: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.xl,
    lineHeight: 20 },
  modeOption: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.md,
    gap: 16 },
  modeIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: `${merchantColors.primary}15`,
    justifyContent: 'center',
    alignItems: 'center' },
  modeOptionInfo: {
    flex: 1 },
  modeOptionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 4 },
  modeOptionDesc: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    marginBottom: 8 },
  joinCodeRow: {
    flexDirection: 'column',
    gap: 8,
    marginTop: 8 },
  joinCodeInput: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 12,
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    color: theme.colors.text.primary,
    borderWidth: 1,
    borderColor: theme.colors.ui.border,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    letterSpacing: 4 },
  joinButton: {
    backgroundColor: merchantColors.primary,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: theme.borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center' },
  joinButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600' },
  cancelButton: {
    alignItems: 'center',
    padding: theme.spacing.md },
  cancelButtonText: {
    fontSize: 16,
    color: theme.colors.text.secondary },
  // Search row with browse button
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    margin: theme.spacing.md },
  browseButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1.5,
    borderColor: merchantColors.primary,
    backgroundColor: '#fff' },
  browseButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: merchantColors.primary },
  // Inventory modal
  inventoryOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24 },
  inventoryModal: {
    backgroundColor: '#fff',
    borderRadius: theme.borderRadius.lg,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
    overflow: 'hidden' },
  inventoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0' },
  inventoryTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1a1a1a' },
  inventoryLoading: {
    padding: 48,
    alignItems: 'center' },
  inventoryList: {
    flex: 1 },
  inventoryEmpty: {
    padding: 48,
    alignItems: 'center' },
  inventoryEmptyText: {
    fontSize: 14,
    color: '#999' },
  inventoryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f5f5f5',
    gap: 12 },
  inventoryItemInCart: {
    backgroundColor: `${merchantColors.accent}08` },
  inventoryThumb: {
    width: 44,
    height: 44,
    borderRadius: 6,
    backgroundColor: '#f4f4f4' },
  inventoryItemInfo: {
    flex: 1,
    minWidth: 0 },
  inventoryItemTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a' },
  inventoryItemBarcode: {
    fontSize: 11,
    color: '#999',
    marginTop: 2 },
  inventoryItemPrice: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1a1a1a',
    marginRight: 8 },
  // Receipt modal
  receiptOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24 },
  receiptModal: {
    backgroundColor: '#fff',
    borderRadius: theme.borderRadius.lg,
    width: '100%',
    maxWidth: 420,
    overflow: 'hidden' },
  receiptSuccess: {
    alignItems: 'center',
    paddingTop: 32,
    paddingBottom: 24,
    paddingHorizontal: 24,
    backgroundColor: `${merchantColors.accent}10` },
  receiptCheckCircle: {
    marginBottom: 12 },
  receiptSuccessTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 4 },
  receiptSuccessAmount: {
    fontSize: 32,
    fontWeight: '800',
    color: merchantColors.primary,
    marginBottom: 2 },
  receiptSuccessItems: {
    fontSize: 14,
    color: '#888' },
  receiptItems: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0' },
  receiptItemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6 },
  receiptItemRowBorder: {
    borderTopWidth: 1,
    borderTopColor: '#f8f8f8' },
  receiptItemName: {
    flex: 1,
    fontSize: 13,
    color: '#444',
    marginRight: 8 },
  receiptItemPrice: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1a1a1a' },
  receiptSection: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0' },
  receiptSectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 4 },
  receiptSectionHint: {
    fontSize: 13,
    color: '#999',
    marginBottom: 16 },
  receiptEmailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12 },
  receiptEmailInput: {
    flex: 1,
    backgroundColor: '#f6f6f6',
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    color: '#1a1a1a' },
  receiptEmailSend: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: merchantColors.primary,
    justifyContent: 'center',
    alignItems: 'center' },
  receiptEmailSendDisabled: {
    backgroundColor: '#ddd' },
  receiptOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: theme.borderRadius.md,
    backgroundColor: '#f9f9f9' },
  receiptOptionInfo: {
    flex: 1 },
  receiptOptionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a' },
  receiptOptionHint: {
    fontSize: 12,
    color: '#999',
    marginTop: 2 },
  receiptQRScanner: {
    height: 200,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: '#000' },
  receiptQRCamera: {
    width: '100%',
    height: '100%' },
  receiptQRClose: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center' },
  receiptQRPermission: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    padding: 16 },
  receiptQRPermissionBtn: {
    backgroundColor: merchantColors.primary,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: 16,
    paddingVertical: 8 },
  receiptActions: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    alignItems: 'center' },
  receiptSkipBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 20 },
  receiptSkipText: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.text.secondary },
  txBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: `${merchantColors.primary}30`,
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 8 },
  txBarLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    flexWrap: 'wrap' },
  txBarTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: merchantColors.primary },
  txBuyerChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: `${merchantColors.accent}20`,
    borderRadius: 20,
    paddingHorizontal: 8,
    paddingVertical: 2 },
  txBuyerChipText: {
    fontSize: 11,
    fontWeight: '600',
    color: merchantColors.accent },
  txBarRight: {
    alignItems: 'flex-end' },
  txBarAmount: {
    fontSize: 15,
    fontWeight: '700',
    color: merchantColors.primary },
  txBarItems: {
    fontSize: 11,
    color: theme.colors.text.secondary } })
